import React, { Fragment, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Link,
} from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useLocation, Location, NavigateFunction } from "react-router-dom";
import AuthLayout from "../common/AuthLayout/AuthLayout";
import DotIcon from "@mui/icons-material/FiberManualRecord";
import DoneIcon from "@mui/icons-material/Done";
import styles from "./ChangePasswordPage.module.css";
import { ChangePasswordVariables, ChangePassword } from "./types/ChangePassword";
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from "@apollo/client/react";
import { ApolloClient, OperationVariables } from "@apollo/client";
import { ServerError, CombinedGraphQLErrors } from "@apollo/client/errors";
import get from "lodash/get";
import changePasswordMutation from "./ChangePasswordMutation";
import { ChangePasswordFormValues, validatePassword, validationRules } from "./PasswordValidation";
import authService, { SET_AUTH } from "../../services/authService";
import { GraphQLError } from "graphql";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { GetOptions } from "../App/types/GetOptions";
import GetOptionsQuery from "../App/GetOptionsQuery";
import { getCpdProviders } from "../LoginPage/types/getCpdProviders";
import GetCpdProvidersQuery from "../LoginPage/GetCpdProvidersQuery";
import StandardizedHeader from "../GeneralHeader/StandardizedHeader";


type ChangePasswordBoxType = {
  onSubmit: (username: string, oldpassword: string, newpassword: string) => Promise<void>;
  error?: any;
  loading?: boolean;
  location: Location | undefined;
  navigate: NavigateFunction | undefined;
  success: boolean;
};

export const ChangePasswordBox: React.FC<ChangePasswordBoxType> = (props) => {
  const { error, loading, location, navigate } = props;
  const username: string = authService.getUsername();
  const hist = useNavigate();
  const currentLocation = useLocation();
  const client = useApolloClient();

  const initialValues: ChangePasswordFormValues = {
    oldpassword: "",
    password: "",
    passwordconfirm: "",
    message: "",
  };

  const [values, setValues] = React.useState({
    oldpassword: "",
    password: "",
    passwordconfirm: "",
    showOldPassword: false,
    showPassword: false,
    showPasswordConfirm: false,
  });

  const submit = (values: ChangePasswordFormValues) => {
    try {
      props.onSubmit(username, values.oldpassword, values.password);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("password didnt change" + e);
    }
  };

  const handleClickShowOldPassword = () => {
    setValues({ ...values, showOldPassword: !values.showOldPassword });
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleClickShowPasswordConfrim = () => {
    setValues({ ...values, showPasswordConfirm: !values.showPasswordConfirm });
  };

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const handleSuccess = () => {
    if (navigate !== undefined) {
      const from = get(location, "state.from") || "/patients";
      navigate(from);
    }
  };
  return (
    <AuthLayout title="" hideHeading maxWidth={"972px"}>
      <Box mt={4}>
        <Formik initialValues={initialValues} validate={validatePassword} onSubmit={submit}>
          {({ errors, handleChange, handleBlur, handleSubmit, touched }) => {
            const handleCancel = async () => {
              if (authService.getExpired()) {
                authService.clearAll();
                await client.resetStore();
                // TODO: shouldn't need to explicitly navigate to '/' if we are behind a <PrivateRoute />
                hist("/login");
              } else if (get(currentLocation, "state.from")) {
                hist(get(currentLocation, "state.from", ""));
              } else {
                hist(-1);
              }
            };
            return (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box>
                      <TextField
                        id="oldpassword"
                        name="oldpassword"
                        label="Old password"
                        fullWidth
                        type={values.showOldPassword ? "text" : "password"}
                        variant="outlined"
                        onChange={handleChange}
                        error={!!errors.oldpassword && touched.oldpassword}
                        onBlur={handleBlur}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowOldPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {values.showOldPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            ),
                          },
                        }}
                        disabled={authService.isCognito()}
                      ></TextField>
                    </Box>
                    <Box mt={2}>
                      <TextField
                        id="password"
                        name="password"
                        fullWidth
                        label="New Password"
                        type={values.showPassword ? "text" : "password"}
                        variant="outlined"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.password && touched.password}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {values.showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            ),
                          },
                        }}
                        disabled={authService.isCognito()}
                      ></TextField>
                    </Box>
                    <Box>
                      {errors.message && touched.passwordconfirm && (
                        <Typography color="error">{errors.message}</Typography>
                      )}
                    </Box>
                    <Box mt={2}>
                      <TextField
                        id="passwordconfirm"
                        name="passwordconfirm"
                        fullWidth
                        label="Confirm password"
                        type={values.showPasswordConfirm ? "text" : "password"}
                        variant="outlined"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.passwordconfirm && touched.passwordconfirm}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPasswordConfrim}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {values.showPasswordConfirm ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            ),
                          },
                        }}
                        disabled={authService.isCognito()}
                      ></TextField>
                    </Box>
                    <Box>
                      {error && (() => {
                        if (CombinedGraphQLErrors.is(error)) {
                          return error.errors.map((graphqlError, index) => (
                            <Typography key={index} color="error">
                              {graphqlError.message}
                            </Typography>
                          ));
                        } else if (ServerError.is(error)) {
                          return <Typography color="error">{error.message}</Typography>;
                        } else {
                          return <Typography color="error">{(error as any)?.message || 'An error occurred'}</Typography>;
                        }
                      })()}
                    </Box>
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Link color="primary" onClick={handleCancel} className={styles.backLink}>
                        Cancel
                      </Link>
                      <Button type="submit" color="primary" variant="contained" disabled={loading || authService.isCognito()}>
                        Change password and log in
                      </Button>
                    </Box>
                    {props.success ? (
                      <>
                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography className={styles.valid}>Your password successfully changed</Typography>
                          <Button type="button" color="primary" variant="contained" onClick={handleSuccess}>
                            OK
                          </Button>
                        </Box>
                      </>
                    ) : (
                      ""
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography>Password requirements</Typography>
                    <List className={styles.list} component="nav" aria-label="main mailbox folders">
                      {validationRules.map((v, i) => (
                        <ListItem disableGutters className={styles.listItem} key={i}>
                          <ListItemIcon className={styles.listIcon}>
                            {v.valid === 1 ? (
                              <DoneIcon className={styles.listIconDone} />
                            ) : (
                              <DotIcon className={`${styles.listIconDot} ${v.valid === -1 ? styles.invalid : ""} `} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primaryTypographyProps={{ variant: "body1" }}
                            className={`${styles.listText} ${v.valid === -1 ? styles.invalid : ""}`}
                            primary={v.text}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>
      </Box>
    </AuthLayout>
  );
};

const ChangePasswordPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const client: ApolloClient = useApolloClient();
  const [changePassword, { loading, error }] = useMutation<ChangePassword, ChangePasswordVariables>(
    changePasswordMutation,
  );
  const [siteOptions, { loading: optionsLoading }] = useLazyQuery<GetOptions>(GetOptionsQuery);
  const [cpdProviders] = useLazyQuery<getCpdProviders, OperationVariables>(GetCpdProvidersQuery);

  const onSubmit =
    (
      location: Location | undefined,
      navigate: NavigateFunction | undefined,
      changePassword: useMutation.ResultTuple<ChangePassword, ChangePasswordVariables>[0],
      client: ApolloClient,
    ) =>
    async (username: string, oldpassword: string, newpassword: string) => {
      try {
        
        const result = await changePassword({
          variables: {
            input: {
              username,
              oldpassword,
              newpassword,
              setCookie: true,
            },
          },
        });

        if (result.data) {
          // console.log(result.data.changePassword.accessToken);
          authService.persistAllValues(result.data.changePassword);

          //always set to non-expired if login via delegate.
          authService.clearExpired();

          client.cache.writeQuery({
            query: SET_AUTH,
            data: { auth: { isAuth: true, __typename: "Auth" } },
          });

          // Get site options
          const options = await siteOptions({});
          if (options.data) {
            authService.setSiteOptions(options.data.getOptions);
          }
          let otpRequired = false;
          if (
            authService.getMFAenrolled() == true &&
            authService.getOtpVerfiedEnrolled() == false &&
            authService.getForceAuth() == true
          ) {
            otpRequired = true;
          } else if (authService.getForceEnrol()) {
            otpRequired = true;
          }

          if (!otpRequired) {
            await cpdProviders().then((v) => {
              if (v.data) {
                authService.setCpdProviders(v.data.getCpdProviders);
              }
            });
          }
        }

        authService.clearExpired();

        setSuccess(true);
      } catch (e) {
        if (
          e.graphQLErrors &&
          (e.graphQLErrors as GraphQLError[]).find((m) => m.message === "Cannot Change Password - Shared Account") !==
            undefined
        ) {
          authService.setSharedAccount(true);
          window.location.reload();
        }
      }
    };

  return (
    <Fragment>
      <StandardizedHeader title={"Change Password"} />
      <ChangePasswordBox
        onSubmit={onSubmit(location, navigate, changePassword, client)}
        error={error}
        loading={loading || optionsLoading}
        location={location}
        navigate={navigate}
        success={success}
      />
    </Fragment>
  );
};

export default ChangePasswordPage;
